import React, { useEffect, useState } from "react";
import { fmtIST } from "../lib/time";
import { api, formatApiErrorDetail } from "../lib/api";
import useSubmitGuard from "../lib/useSubmitGuard";
import PageHeader from "../components/PageHeader";
import { Plus, Trash2, History, Search, Pencil, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { Dialog, Input } from "./Suppliers";
import { useAuth } from "../context/AuthContext";

export default function InspectionTemplates() {
  const { busy: submitBusy, guard: submitGuard, reset: resetGuard } = useSubmitGuard();
  const [materials, setMaterials] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [materialId, setMaterialId] = useState("");
  const [params, setParams] = useState([]);
  const [history, setHistory] = useState(null); // { material_id, list }
  const [editTpl, setEditTpl] = useState(null); // { id, parameters, remark }
  const [q, setQ] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const { user } = useAuth();

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/bulk-import/templates.xlsx", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data]));
      a.download = "templates_template.xlsx";
      a.click();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed"); }
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/bulk-import/templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const s = r.data;
      toast.success(`Imported: ${s.created} new templates · ${s.skipped} skipped`);
      if ((s.errors || []).length) toast.error(`Errors: ${s.errors.slice(0, 3).map((x) => `${x.material_code || ""}: ${x.error}`).join(" · ")}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed"); }
    finally { setImportBusy(false); e.target.value = ""; }
  };

  const load = async () => {
    const [m, t] = await Promise.all([api.get("/materials"), api.get("/templates")]);
    setMaterials(m.data); setTemplates(t.data);
  };
  useEffect(() => { load(); }, []);

  const matName = (id) => materials.find((m) => m.id === id)?.name || "—";
  const matCode = (id) => materials.find((m) => m.id === id)?.code || "—";

  const addParam = () => setParams([...params, { name: "", specification: "", method: "", result_type: "pass_fail", mandatory: true, sample_size: 1 }]);
  const removeParam = (i) => setParams(params.filter((_, idx) => idx !== i));
  const updateParam = (i, patch) => setParams(params.map((p, idx) => idx === i ? { ...p, ...patch } : p));

  const openNew = () => { setMaterialId(""); setParams([{ name: "", specification: "", method: "", result_type: "pass_fail", mandatory: true, sample_size: 1 }]); setOpen(true); };

  const save = async () => {
    if (!materialId) { toast.error("Select material"); return; }
    try {
      await submitGuard(async (idemKey) => {
        await api.post("/templates", { material_id: materialId, parameters: params }, { headers: { "Idempotency-Key": idemKey } });
      });
      setOpen(false);
      toast.success("Template created (new version)");
      resetGuard();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete template version ${t.version} for ${matCode(t.material_id)}? (Will be soft-deleted; cannot delete if linked to inspections.)`)) return;
    try { await api.delete(`/templates/${t.id}`); toast.success("Template deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openEdit = (t) => {
    setEditTpl({
      id: t.id,
      version: t.version,
      material_id: t.material_id,
      parameters: (t.parameters || []).map((p) => ({ ...p })),
      remark: "",
    });
  };

  const updateEditParam = (i, patch) => setEditTpl((p) => ({ ...p, parameters: p.parameters.map((x, idx) => idx === i ? { ...x, ...patch } : x) }));
  const addEditParam = () => setEditTpl((p) => ({ ...p, parameters: [...p.parameters, { name: "", specification: "", method: "", result_type: "pass_fail", mandatory: true, sample_size: 1 }] }));
  const removeEditParam = (i) => setEditTpl((p) => ({ ...p, parameters: p.parameters.filter((_, idx) => idx !== i) }));

  const saveEdit = async () => {
    if (!(editTpl.remark || "").trim()) { toast.error("Remark is required for in-place edit"); return; }
    if (!editTpl.parameters.length) { toast.error("At least one parameter required"); return; }
    try {
      const isSA = !!user?.role?.is_super_admin;
      await api.put(`/templates/${editTpl.id}`, { parameters: editTpl.parameters, remark: editTpl.remark });
      toast.success(isSA ? "Template updated" : "Edit submitted for SA approval");
      setEditTpl(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const approveTplEdit = async (t) => {
    try { await api.post(`/templates/${t.id}/approve-edit`); toast.success("Edit approved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const rejectTplEdit = async (t) => {
    const remark = window.prompt(`Reason for rejecting this edit?`);
    if (!remark) return;
    try { await api.post(`/templates/${t.id}/reject-edit`, { remark }); toast.success("Edit rejected"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const showHistory = async (mid) => {
    try {
      const r = await api.get(`/templates/history/${mid}`);
      setHistory({ material_id: mid, list: r.data });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const canEdit = user?.role?.is_super_admin || user?.role?.name === "Quality Inspector";

  const filtered = templates.filter((t) => {
    if (!q) return true;
    const k = q.toLowerCase();
    return matCode(t.material_id).toLowerCase().includes(k) || matName(t.material_id).toLowerCase().includes(k);
  });

  return (
    <div data-testid="templates-page">
      <PageHeader title="Inspection Templates" subtitle="Per-material QC checklists · QA can create, edit, delete"
        helpText="Quality Inspector or above can create new versions. Versioning is automatic — the highest version is used by inspections. Templates linked to existing inspections cannot be deleted (soft delete only)."
        right={canEdit && (
          <div className="flex gap-2">
            <button onClick={downloadTemplate} className="border border-[#1565C0] text-[#1565C0] text-xs px-3 py-2 rounded-sm flex items-center gap-1 uppercase tracking-wider font-bold" data-testid="tpl-download-template"><Download size={12}/> Template</button>
            <label className={`border border-[#2E7D32] text-[#2E7D32] text-xs px-3 py-2 rounded-sm flex items-center gap-1 uppercase tracking-wider font-bold cursor-pointer ${importBusy ? "opacity-50 pointer-events-none" : ""}`} data-testid="tpl-bulk-import-btn">
              <Upload size={12}/> {importBusy ? "Importing…" : "Bulk Import"}
              <input type="file" accept=".xlsx,.xls" onChange={importExcel} className="hidden"/>
            </label>
            <button onClick={openNew} className="bg-[#1565C0] hover:bg-[#0D2B5E] text-white text-sm px-4 py-2 rounded-sm flex items-center gap-2 uppercase tracking-wider font-semibold" data-testid="new-template-btn"><Plus size={14}/> New Template</button>
          </div>
        )}
      />
      <div className="p-8 space-y-4">
        <div className="relative max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D2B5E]/40"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="tpl-search" placeholder="Search material code / name…" className="w-full pl-9 pr-3 py-2 border border-[#EEEEEE] rounded-sm text-sm bg-white"/>
        </div>
        {filtered.map((t) => (
          <div key={t.id} className="bg-white border border-[#EEEEEE] rounded-sm overflow-hidden" data-testid={`tpl-${t.id}`}>
            <div className="px-5 py-3 border-b border-[#EEEEEE] flex items-center justify-between bg-[#F5F5F5]">
              <div>
                <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Material</div>
                <div className="text-sm font-medium"><span className="font-mono text-[#1565C0]">{matCode(t.material_id)}</span> — {matName(t.material_id)}</div>
                {t.pending_edit && (
                  <div className="text-[9px] tracking-widest uppercase font-bold bg-[#E65100]/15 text-[#E65100] px-1.5 py-0.5 rounded-sm inline-block mt-1" data-testid={`tpl-pending-${t.id}`}>
                    EDIT PENDING — submitted by {t.pending_edit.submitted_by} · {fmtIST(t.pending_edit.submitted_at)}
                  </div>
                )}
                {/* Wave-23 Ch-1: show the last approval time + approver once a version is live. */}
                {t.approved_at && (
                  <div className="text-[9px] tracking-widest uppercase text-[#2E7D32] mt-1" data-testid={`tpl-approved-${t.id}`}>
                    APPROVED by {t.approved_by || "—"} · {fmtIST(t.approved_at)}
                  </div>
                )}
                {t.updated_at && !t.approved_at && (
                  <div className="text-[9px] tracking-widest uppercase text-[#0D2B5E]/50 mt-1">
                    LAST UPDATED by {t.updated_by || "—"} · {fmtIST(t.updated_at)}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold font-mono">Version {t.version}</div>
                <button onClick={() => showHistory(t.material_id)} title="Version history" className="text-[#1565C0]" data-testid={`tpl-history-${t.material_id}`}><History size={16}/></button>
                {t.pending_edit && user?.role?.is_super_admin && (
                  <>
                    <button onClick={() => approveTplEdit(t)} title="Approve edit" className="text-[#2E7D32] text-xs uppercase tracking-widest font-bold" data-testid={`tpl-approve-${t.id}`}>Approve</button>
                    <button onClick={() => rejectTplEdit(t)} title="Reject edit" className="text-[#C62828] text-xs uppercase tracking-widest font-bold" data-testid={`tpl-reject-${t.id}`}>Reject</button>
                  </>
                )}
                {canEdit && !t.pending_edit && <button onClick={() => openEdit(t)} title={user?.role?.is_super_admin ? "Edit (applies immediately)" : "Edit (submits to SA for approval)"} className="text-[#1565C0]" data-testid={`tpl-edit-${t.id}`}><Pencil size={16}/></button>}
                {canEdit && !t.pending_edit && <button onClick={() => del(t)} title="Delete (soft)" className="text-[#C62828]" data-testid={`tpl-delete-${t.id}`}><Trash2 size={16}/></button>}
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                <tr>
                  <th className="text-left px-4 py-2">Parameter</th>
                  <th className="text-left px-4 py-2">Specification</th>
                  <th className="text-left px-4 py-2">Method</th>
                  <th className="text-left px-4 py-2">Result Type</th>
                  <th className="text-center px-4 py-2">Sample #</th>
                  <th className="text-left px-4 py-2">Mandatory</th>
                </tr>
              </thead>
              <tbody>
                {(t.parameters || []).map((p, i) => (
                  <tr key={i} className={i % 2 === 0 ? "" : "bg-[#F5F5F5]/40"}>
                    <td className="px-4 py-2 font-medium">{p.name}</td>
                    <td className="px-4 py-2 font-mono text-xs">{p.specification}</td>
                    <td className="px-4 py-2 text-xs">{p.method}</td>
                    <td className="px-4 py-2 text-xs uppercase tracking-widest text-[#0D2B5E]/70">{p.result_type}</td>
                    <td className="px-4 py-2 text-xs text-center font-mono font-bold" data-testid={`tpl-samples-${t.id}-${i}`}>{p.sample_size || 1}</td>
                    <td className="px-4 py-2 text-xs">{p.mandatory ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
        {filtered.length === 0 && <div className="text-center text-sm text-[#0D2B5E]/50 py-10">No templates.</div>}
      </div>

      {open && (
        <Dialog title="New Inspection Template" onClose={() => setOpen(false)} wide>
          <div>
            <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Material</label>
            <select value={materialId} onChange={(e) => setMaterialId(e.target.value)} data-testid="tpl-material"
              className="w-full border border-[#EEEEEE] focus:border-[#1565C0] outline-none px-3 py-2 text-sm rounded-sm">
              <option value="">Select material…</option>
              {materials.map((m) => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
            </select>
          </div>
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60">Parameters</div>
              <button onClick={addParam} className="text-xs text-[#1565C0] font-semibold uppercase tracking-wider" data-testid="tpl-add-param">+ Add parameter</button>
            </div>
            <table className="w-full text-sm border border-[#EEEEEE]">
              <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                <tr>
                  <th className="text-left px-2 py-1">Name</th>
                  <th className="text-left px-2 py-1">Specification</th>
                  <th className="text-left px-2 py-1">Method</th>
                  <th className="text-left px-2 py-1">Type</th>
                  <th className="text-left px-2 py-1">Samples</th>
                  <th className="text-left px-2 py-1">Req</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {params.map((p, i) => (
                  <tr key={i}>
                    <td className="p-1"><input value={p.name} onChange={(e) => updateParam(i, { name: e.target.value })} className="w-full border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm"/></td>
                    <td className="p-1"><input value={p.specification} onChange={(e) => updateParam(i, { specification: e.target.value })} className="w-full border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono"/></td>
                    <td className="p-1"><input value={p.method} onChange={(e) => updateParam(i, { method: e.target.value })} className="w-full border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm"/></td>
                    <td className="p-1">
                      <select value={p.result_type} onChange={(e) => updateParam(i, { result_type: e.target.value })} className="w-full border border-[#EEEEEE] px-1 py-1 text-xs rounded-sm">
                        <option value="pass_fail">Pass/Fail</option>
                        <option value="numeric">Numeric</option>
                        <option value="text">Text</option>
                      </select>
                    </td>
                    <td className="p-1">
                      <input type="number" min={1} max={20} value={p.sample_size || 1} onChange={(e) => updateParam(i, { sample_size: Math.max(1, Math.min(20, +e.target.value || 1)) })} data-testid={`tpl-new-sample-${i}`} className="w-16 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono text-center"/>
                    </td>
                    <td className="p-1 text-center">
                      <input type="checkbox" checked={p.mandatory} onChange={(e) => updateParam(i, { mandatory: e.target.checked })} />
                    </td>
                    <td className="p-1">
                      <button onClick={() => removeParam(i)} className="text-[#C62828]"><Trash2 size={14}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
            <button onClick={save} disabled={submitBusy} className="px-4 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-semibold tracking-wider uppercase disabled:opacity-50 disabled:cursor-not-allowed" data-testid="save-template-btn">{submitBusy ? "Saving…" : "Save"}</button>
          </div>
        </Dialog>
      )}
      {history && (
        <Dialog title={`Version history — ${matCode(history.material_id)}`} onClose={() => setHistory(null)} wide>
          <div className="space-y-2">
            {history.list.map((t) => (
              <div key={t.id} className="border border-[#EEEEEE] rounded-sm p-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-bold">Version {t.version}{t.remark ? ` · ${t.remark}` : ""}</div>
                  <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-mono">{fmtIST(t.updated_at || t.created_at)} {t.deleted_at && <span className="text-[#C62828]">· DELETED</span>}</div>
                </div>
                <div className="text-xs text-[#0D2B5E]/70">{(t.parameters || []).map((p) => p.name).join(" · ")}</div>
              </div>
            ))}
            {history.list.length === 0 && <div className="text-sm text-center text-[#0D2B5E]/50 py-6">No history.</div>}
          </div>
        </Dialog>
      )}

      {editTpl && (
        <Dialog title={`Edit Template · ${matCode(editTpl.material_id)} (v${editTpl.version})`} onClose={() => setEditTpl(null)} wide>
          <div className="text-xs text-[#0D2B5E]/70 bg-[#1565C0]/5 border border-[#1565C0]/30 p-2 rounded-sm mb-3">
            In-place edit · Version number is preserved · A mandatory remark is captured as the version label.
          </div>
          <div className="mb-3">
            <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Edit Remark *</label>
            <input value={editTpl.remark || ""} onChange={(e) => setEditTpl({ ...editTpl, remark: e.target.value })} placeholder="e.g. tightened CMM tolerance per supplier feedback" data-testid="tpl-edit-remark"
              className="w-full border border-[#EEEEEE] focus:border-[#1565C0] outline-none px-3 py-2 text-sm rounded-sm"/>
          </div>
          <div className="flex justify-between items-center mb-2">
            <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60">Parameters</div>
            <button onClick={addEditParam} className="text-xs text-[#1565C0] font-semibold uppercase tracking-wider" data-testid="tpl-edit-add-param">+ Add parameter</button>
          </div>
          <table className="w-full text-sm border border-[#EEEEEE]">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-2 py-1">Name</th>
                <th className="text-left px-2 py-1">Specification</th>
                <th className="text-left px-2 py-1">Method</th>
                <th className="text-left px-2 py-1">Type</th>
                <th className="text-left px-2 py-1">Samples</th>
                <th className="text-left px-2 py-1">Req</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {editTpl.parameters.map((p, i) => (
                <tr key={i}>
                  <td className="p-1"><input value={p.name} onChange={(e) => updateEditParam(i, { name: e.target.value })} data-testid={`tpl-edit-name-${i}`} className="w-full border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm"/></td>
                  <td className="p-1"><input value={p.specification} onChange={(e) => updateEditParam(i, { specification: e.target.value })} data-testid={`tpl-edit-spec-${i}`} className="w-full border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono"/></td>
                  <td className="p-1"><input value={p.method} onChange={(e) => updateEditParam(i, { method: e.target.value })} className="w-full border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm"/></td>
                  <td className="p-1">
                    <select value={p.result_type} onChange={(e) => updateEditParam(i, { result_type: e.target.value })} className="w-full border border-[#EEEEEE] px-1 py-1 text-xs rounded-sm">
                      <option value="pass_fail">Pass/Fail</option>
                      <option value="numeric">Numeric</option>
                      <option value="text">Text</option>
                    </select>
                  </td>
                  <td className="p-1">
                    <input type="number" min={1} max={20} value={p.sample_size || 1} onChange={(e) => updateEditParam(i, { sample_size: Math.max(1, Math.min(20, +e.target.value || 1)) })} data-testid={`tpl-edit-sample-${i}`} className="w-16 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono text-center"/>
                  </td>
                  <td className="p-1 text-center">
                    <input type="checkbox" checked={p.mandatory} onChange={(e) => updateEditParam(i, { mandatory: e.target.checked })} />
                  </td>
                  <td className="p-1">
                    <button onClick={() => removeEditParam(i)} className="text-[#C62828]"><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setEditTpl(null)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
            <button onClick={saveEdit} className="px-4 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="tpl-save-edit">Save (in-place)</button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
